import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiPlus, FiCopy, FiEdit, FiTrash2, FiLoader, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import setupInstructionsClient from '@/utils/setupInstructionsClient';
import logger from '@/utils/logger';
import SectionTitle from '@/components/SectionTitle';
import Accordion from '@/components/Accordion';
import InstructionModal from '@/components/InstructionModal';
import Card from '@/components/Card';
import InfoText from '@/components/InfoText';

const platforms = ["GitHub", "NoCodeBackend", "Cloudflare Pages", "Dropbox", "OpenAI", "Cloudflare Env Vars", "Other"];

const copyToClipboard = (text, successMessage) => {
  navigator.clipboard.writeText(text);
  logger.success(successMessage);
};

const DropboxHelperPanel = () => {
    const [cloudflareUrl, setCloudflareUrl] = useState('');
    const [appKey, setAppKey] = useState(import.meta.env.VITE_DROPBOX_APP_KEY || '');
    
    const redirectUri = cloudflareUrl ? `${cloudflareUrl.replace(/\/$/, '')}/oauth/dropbox/callback` : '';
    const authUrl = appKey && redirectUri ? `https://www.dropbox.com/oauth2/authorize?client_id=${encodeURIComponent(appKey)}&response_type=code&token_access_type=offline&redirect_uri=${encodeURIComponent(redirectUri)}` : '';

    return (
        <Card className="mt-4 border border-blue-200 bg-blue-50">
            <h4 className="font-bold text-lg text-gray-800 mb-2">Dropbox OAuth Helper</h4>
            <InfoText>Enter your deployed Cloudflare URL to generate the correct Redirect URI and Authorization URL for Dropbox.</InfoText>
            <div className="space-y-4 mt-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Cloudflare URL</label>
                    <input type="text" value={cloudflareUrl} onChange={e => setCloudflareUrl(e.target.value)} placeholder="https://your-project.pages.dev" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                </div>
                <div>
                     <label className="block text-sm font-medium text-gray-700">Redirect URI (for Dropbox App Console)</label>
                    <div className="flex items-center space-x-2">
                        <input type="text" readOnly value={redirectUri} className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100" />
                        <button onClick={() => copyToClipboard(redirectUri, 'Redirect URI copied!')}><FiCopy /></button>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">App Key</label>
                    <input type="text" value={appKey} onChange={e => setAppKey(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Authorization URL (to generate refresh token)</label>
                    <div className="flex items-center space-x-2">
                        <input type="text" readOnly value={authUrl} className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100" />
                        <button onClick={() => copyToClipboard(authUrl, 'Authorization URL copied!')}><FiCopy /></button>
                        <a href={authUrl} target="_blank" rel="noopener noreferrer" className={!authUrl ? 'pointer-events-none opacity-50' : ''}>
                           <FiExternalLink />
                        </a>
                    </div>
                </div>
            </div>
        </Card>
    );
};


const PlatformSection = ({ platform, instructions, onEdit, onDelete, onCopyAll }) => (
    <div className="bg-white p-4 rounded-lg shadow mb-4">
        <Accordion title={
            <div className="flex justify-between items-center w-full">
                <span className="text-xl font-semibold">{platform}</span>
                <button onClick={(e) => { e.stopPropagation(); onCopyAll(platform, instructions); }} className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200">
                    Copy All
                </button>
            </div>
        }>
            {platform === 'Dropbox' && <DropboxHelperPanel />}
            {instructions.length > 0 ? instructions.map(inst => (
                <div key={inst.id} className="py-2 border-b last:border-b-0">
                    <h4 className="font-bold text-gray-800">{inst.title}</h4>
                    <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans bg-gray-50 p-2 rounded mt-1">{inst.steps}</pre>
                    <div className="flex space-x-2 mt-2">
                        <button onClick={() => copyToClipboard(`${inst.title}\n\n${inst.steps}`, 'Instruction copied!')} className="text-gray-500 hover:text-gray-800"><FiCopy /></button>
                        <button onClick={() => onEdit(inst)} className="text-gray-500 hover:text-gray-800"><FiEdit /></button>
                        <button onClick={() => onDelete(inst.id, platform)} className="text-red-500 hover:text-red-800"><FiTrash2 /></button>
                    </div>
                </div>
            )) : <p className="text-sm text-gray-500 py-4">No instructions for this platform yet.</p>}
        </Accordion>
    </div>
);


function SetupGuides() {
    const [instructions, setInstructions] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingInstruction, setEditingInstruction] = useState(null);

    const fetchInstructions = useCallback(async () => {
        setLoading(true);
        try {
            const allInstructions = await setupInstructionsClient.getInstructions();
            const grouped = allInstructions.reduce((acc, inst) => {
                const platform = inst.platform || 'Other';
                if (!acc[platform]) acc[platform] = [];
                acc[platform].push(inst);
                return acc;
            }, {});
            setInstructions(grouped);
            setError(null);
        } catch (err) {
            setError(err.message);
            logger.error('Failed to fetch instructions', { error: err.message });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInstructions();
    }, [fetchInstructions]);
    
    const handleOpenModal = (instruction = null) => {
        setEditingInstruction(instruction);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingInstruction(null);
    };

    const handleSaveInstruction = async (data) => {
        try {
            if (editingInstruction) {
                await setupInstructionsClient.updateInstruction(editingInstruction.id, data);
                logger.success('Instruction updated successfully');
            } else {
                await setupInstructionsClient.createInstruction(data);
                logger.success('Instruction created successfully');
            }
            handleCloseModal();
            fetchInstructions();
        } catch (err) {
            logger.error('Failed to save instruction', { error: err.message });
        }
    };

    const handleDelete = async (id, platform) => {
        if (window.confirm('Are you sure you want to delete this instruction?')) {
            try {
                await setupInstructionsClient.deleteInstruction(id);
                logger.success('Instruction deleted');
                setInstructions(prev => ({
                    ...prev,
                    [platform]: prev[platform].filter(inst => inst.id !== id),
                }));
            } catch (err) {
                logger.error('Failed to delete instruction', { error: err.message });
            }
        }
    };
    
    const handleCopyAll = (platform, platformInstructions) => {
        const text = `PLATFORM: ${platform}\n\n` + platformInstructions.map(inst => `${inst.title}\n${inst.steps}`).join('\n\n---\n\n');
        copyToClipboard(text, `All instructions for ${platform} copied!`);
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <InstructionModal isOpen={isModalOpen} onClose={handleCloseModal} onSave={handleSaveInstruction} instruction={editingInstruction} />
            <div className="flex justify-between items-center mb-6">
                <SectionTitle title="Setup Guides" subtitle="Step-by-step guides for configuring all services." />
                <button onClick={() => handleOpenModal()} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                    <FiPlus className="mr-2" /> Add Instruction
                </button>
            </div>
            
            {loading && <div className="text-center"><FiLoader className="animate-spin h-8 w-8 mx-auto text-indigo-600" /></div>}
            {error && <div className="text-red-500 text-center"><FiAlertTriangle className="inline mr-2" /> Error: {error}</div>}
            
            {!loading && !error && (
                <div>
                    {platforms.map(platform => (
                        <PlatformSection
                            key={platform}
                            platform={platform}
                            instructions={instructions[platform] || []}
                            onEdit={handleOpenModal}
                            onDelete={handleDelete}
                            onCopyAll={handleCopyAll}
                        />
                    ))}
                </div>
            )}
        </motion.div>
    );
}

export default SetupGuides;